import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertTriangle,
  Brain,
  Heart,
  Zap,
  RefreshCw,
  Target,
  Shield,
  Sparkles,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  FileText,
  ArrowRight,
  Crosshair,
  Lightbulb,
  ShieldCheck,
  Clock,
  TrendingUp,
  XCircle,
  MessageSquare,
} from "lucide-react";
import { StrategicProcessingSequence } from "@/components/processing/strategic-processing-sequence";
import type {
  FunnelStrategyData,
  NarrativeOpportunity,
  NarrativeAngle,
} from "@/lib/campaign-draft";
import type { Product, Persona, Brand } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface FunnelStrategyStepProps {
  brandId: string;
  brand: Brand | null;
  product: Product | null;
  persona: Persona | null;
  narrativeOpportunity: NarrativeOpportunity | null;
  narrativeAngles: NarrativeAngle[];
  funnelStrategy: FunnelStrategyData | null;
  onUpdate: (strategy: FunnelStrategyData | null) => void;
  isStale: boolean;
  onClearStale: () => void;
}

const STAGE_GROUP_CONFIG: Record<string, { label: string; icon: typeof Brain; color: string; bgColor: string }> = {
  Cognitive: { label: "Cognitive", icon: Brain, color: "text-blue-500 dark:text-blue-400", bgColor: "bg-blue-500/10" },
  Affective: { label: "Affective", icon: Heart, color: "text-pink-500 dark:text-pink-400", bgColor: "bg-pink-500/10" },
  Behavioral: { label: "Behavioral", icon: Zap, color: "text-amber-500 dark:text-amber-400", bgColor: "bg-amber-500/10" },
};

export function FunnelStrategyStep({
  brandId,
  brand,
  product,
  persona,
  narrativeOpportunity,
  narrativeAngles,
  funnelStrategy,
  onUpdate,
  isStale,
  onClearStale,
}: FunnelStrategyStepProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [apiReady, setApiReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedStage, setExpandedStage] = useState<string | null>(null);
  const { toast } = useToast();

  const validationErrors = getValidationErrors(product, persona, brand, narrativeOpportunity, narrativeAngles);
  const canGenerate = validationErrors.length === 0;

  const handleGenerate = useCallback(async () => {
    if (!canGenerate) return;
    setIsGenerating(true);
    setApiReady(false);
    setError(null);

    try {
      if (!brand) {
        setError("Brand data not loaded yet. Please wait and try again.");
        setIsGenerating(false);
        return;
      }

      const res = await apiRequest("POST", "/api/funnel-strategy", {
        brandId,
        product,
        persona,
        brand,
        narrative_opportunity: narrativeOpportunity,
        selected_narrative_angles: narrativeAngles,
      });
      const data = await res.json();

      const summary = data?.strategy_summary;
      const stages = data?.funnel_stages;

      if (!summary || typeof summary !== "object") {
        console.error("[FunnelStrategy] Missing strategy_summary. Response keys:", Object.keys(data || {}));
        throw new Error("Response missing strategy_summary");
      }
      if (!Array.isArray(stages) || stages.length === 0) {
        console.error("[FunnelStrategy] Missing/empty funnel_stages. Response keys:", Object.keys(data || {}));
        throw new Error("Response missing or empty funnel_stages");
      }

      const strategyData: FunnelStrategyData = {
        strategy_summary: summary,
        stages,
        generatedAt: new Date().toISOString(),
      };

      setApiReady(true);

      setTimeout(() => {
        onUpdate(strategyData);
        onClearStale();
        setIsGenerating(false);
        toast({
          title: "Funnel strategy generated",
          description: `${stages.length} stages mapped across the full funnel`,
        });
      }, 500);
    } catch (err: any) {
      console.error("[FunnelStrategy] Generation failed:", err);
      const msg = err?.message || "";
      setError(
        msg.includes("strategy_summary") || msg.includes("funnel_stages")
          ? "The strategy API returned an unexpected format. Please try again."
          : msg || "Unable to generate funnel strategy right now. Please try again."
      );
      setIsGenerating(false);
    }
  }, [canGenerate, product, persona, brand, brandId, narrativeOpportunity, narrativeAngles, onUpdate, onClearStale, toast]);

  if (isGenerating) {
    return (
      <StrategicProcessingSequence
        title="Mapping Full Funnel Strategy"
        subtitle="Aligning messaging across awareness, knowledge, affinity, conviction, and conversion"
        isRunning={isGenerating}
        onComplete={() => {}}
        waitForApi={!apiReady}
        phases={FUNNEL_PHASES}
        statusMessages={FUNNEL_STATUS_MESSAGES}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="step-funnel-strategy">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold tracking-tight" data-testid="text-funnel-strategy-title">
            Funnel Strategy
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Define your messaging strategy across all funnel stages
          </p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={!canGenerate}
          data-testid="button-generate-funnel-strategy"
        >
          {funnelStrategy ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Strategy
            </>
          )}
        </Button>
      </div>

      {isStale && funnelStrategy && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3" data-testid="alert-stale">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
          <span className="text-sm text-destructive">
            Upstream selections changed. Consider regenerating your funnel strategy.
          </span>
        </div>
      )}

      {!canGenerate && validationErrors.length > 0 && (
        <div className="rounded-md bg-muted/50 p-3 space-y-1" data-testid="validation-errors">
          {validationErrors.map((msg, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              <span>{msg}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <Card className="p-4 border-destructive/30 bg-destructive/5" data-testid="error-card">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive">Generation failed</p>
              <p className="text-xs text-muted-foreground mt-1">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={handleGenerate}
                disabled={!canGenerate}
                data-testid="button-retry"
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Retry
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!funnelStrategy ? (
        <Card className="border-dashed">
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/60 mb-4">
              <FileText className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <p className="text-muted-foreground text-center text-sm font-medium" data-testid="text-funnel-empty">
              Generate a funnel strategy to define messaging for each stage
            </p>
            <p className="text-muted-foreground/60 text-center text-xs mt-1.5 max-w-sm">
              Your persona, product, and narrative context will be analyzed to create a stage-by-stage persuasion framework
            </p>
          </div>
        </Card>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-0"
          >
            <Card className="rounded-2xl border-primary/15 overflow-hidden" data-testid="card-strategy-report">
              <ReportHeader generatedAt={funnelStrategy.generatedAt} stageCount={funnelStrategy.stages.length} />
              <StrategySummarySection summary={funnelStrategy.strategy_summary} />
              <div className="border-t border-border/40">
                <div className="px-6 py-4 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Funnel Stages</span>
                    <Badge variant="secondary" className="text-[10px] ml-auto">{funnelStrategy.stages.length} stages</Badge>
                  </div>
                </div>

                {groupStagesByGroup(funnelStrategy.stages).map(([group, stages], groupIdx) => {
                  const config = STAGE_GROUP_CONFIG[group] || STAGE_GROUP_CONFIG.Cognitive;
                  const Icon = config.icon;
                  return (
                    <div key={group}>
                      <div className="px-6 py-3 border-t border-border/30 bg-muted/15">
                        <div className="flex items-center gap-2">
                          <div className={`flex h-5 w-5 items-center justify-center rounded-md ${config.bgColor}`}>
                            <Icon className={`h-3 w-3 ${config.color}`} />
                          </div>
                          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {config.label} Phase
                          </span>
                          <span className="text-[10px] text-muted-foreground/60 ml-1">
                            {stages.length} {stages.length === 1 ? "stage" : "stages"}
                          </span>
                        </div>
                      </div>
                      {stages.map((stage, idx) => (
                        <StageCard
                          key={stage.stage}
                          stage={stage}
                          stageNumber={funnelStrategy.stages.indexOf(stage) + 1}
                          angles={narrativeAngles}
                          isExpanded={expandedStage === stage.stage}
                          onToggle={() => setExpandedStage(expandedStage === stage.stage ? null : stage.stage)}
                          delay={idx * 0.08}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
}

function ReportHeader({ generatedAt, stageCount }: { generatedAt: string; stageCount: number }) {
  return (
    <div className="px-6 py-5 bg-gradient-to-br from-primary/[0.06] via-primary/[0.03] to-transparent" data-testid="card-strategy-summary">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20">
            <FileText className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold tracking-tight">Funnel Strategy Report</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{stageCount}-stage persuasion framework</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground/70 shrink-0 pt-1">
          <Clock className="h-3 w-3" />
          <span className="text-[10px]">{formatTime(generatedAt)}</span>
        </div>
      </div>
    </div>
  );
}

function StrategySummarySection({ summary }: { summary: FunnelStrategyData["strategy_summary"] }) {
  return (
    <div className="px-6 py-5 border-t border-border/40">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-3.5 w-3.5 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Strategic Overview</span>
      </div>

      <div className="space-y-4">
        <SummaryBlock
          icon={Crosshair}
          label="Core Tension"
          value={summary.core_tension}
          accent="text-red-500 dark:text-red-400"
          accentBg="bg-red-500/10"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SummaryBlock
            icon={TrendingUp}
            label="Desired Transformation"
            value={summary.desired_transformation}
            accent="text-emerald-500 dark:text-emerald-400"
            accentBg="bg-emerald-500/10"
          />
          <SummaryBlock
            icon={Lightbulb}
            label="Primary Promise"
            value={summary.primary_promise}
            accent="text-amber-500 dark:text-amber-400"
            accentBg="bg-amber-500/10"
          />
        </div>

        <SummaryBlock
          icon={Shield}
          label="Conversion Friction"
          value={summary.main_conversion_friction}
          accent="text-orange-500 dark:text-orange-400"
          accentBg="bg-orange-500/10"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SummaryBlock
            icon={MessageSquare}
            label="Brand Role"
            value={summary.brand_role}
            accent="text-violet-500 dark:text-violet-400"
            accentBg="bg-violet-500/10"
          />
          <div className="rounded-xl bg-muted/40 p-4">
            <div className="flex items-center gap-2 mb-2.5">
              <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10">
                <ShieldCheck className="h-3 w-3 text-primary" />
              </div>
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                Best Proof Types
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              {summary.best_proof_types.map((pt, i) => (
                <div key={pt} className="flex items-start gap-2 text-[12px] text-foreground/80 leading-relaxed">
                  <span className="text-primary/50 shrink-0 mt-px">&#x2022;</span>
                  <span>{pt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryBlock({
  icon: Icon,
  label,
  value,
  accent,
  accentBg,
}: {
  icon: typeof Brain;
  label: string;
  value: string;
  accent: string;
  accentBg: string;
}) {
  return (
    <div className="rounded-xl bg-muted/40 p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`flex h-5 w-5 items-center justify-center rounded-md ${accentBg}`}>
          <Icon className={`h-3 w-3 ${accent}`} />
        </div>
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-[13px] text-foreground/85 leading-relaxed">{value}</p>
    </div>
  );
}

function StageCard({
  stage,
  stageNumber,
  angles,
  isExpanded,
  onToggle,
  delay,
}: {
  stage: FunnelStrategyData["stages"][0];
  stageNumber: number;
  angles: NarrativeAngle[];
  isExpanded: boolean;
  onToggle: () => void;
  delay: number;
}) {
  const resolvedAngles = stage.recommended_angle_ids.map((id) => {
    const found = angles.find((a) => a.id === id);
    return {
      id,
      name: found ? found.name : id,
      description: found?.summary ?? found?.coreTension ?? null,
    };
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
    >
      <div
        className={`cursor-pointer transition-colors hover:bg-muted/30 ${isExpanded ? "bg-muted/20" : ""}`}
        onClick={onToggle}
        data-testid={`card-funnel-stage-${stage.stage}`}
      >
        <div className="px-6 py-4 border-t border-border/20">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/8 ring-1 ring-primary/15 shrink-0">
                <span className="text-xs font-bold text-primary">{stageNumber}</span>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold">{stage.stage}</h4>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{stage.objective}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {resolvedAngles.length > 0 && !isExpanded && (
                <Badge variant="outline" className="text-[9px] hidden sm:inline-flex">
                  {resolvedAngles.length} {resolvedAngles.length === 1 ? "angle" : "angles"}
                </Badge>
              )}
              <div className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-muted/50">
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <StageDetailField icon={Brain} label="Psychological Task" value={stage.psychological_task} />
                  <StageDetailField icon={Crosshair} label="Messaging Focus" value={stage.messaging_focus} />
                  <StageDetailField icon={Heart} label="Emotional Job" value={stage.emotional_job} />
                  <StageDetailField icon={Shield} label="Proof Focus" value={stage.proof_focus} />
                  <StageDetailField icon={Sparkles} label="Message Style" value={stage.message_style} />
                  <StageDetailField icon={XCircle} label="What Not to Say Yet" value={stage.what_not_to_say_yet} />
                </div>

                {resolvedAngles.length > 0 && (
                  <div className="rounded-lg bg-muted/30 p-3">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block mb-2">
                      Recommended Angles
                    </span>
                    <TooltipProvider delayDuration={150}>
                      <div className="flex flex-wrap gap-1.5">
                        {resolvedAngles.map((angle, i) => {
                          const badge = (
                            <Badge
                              variant="secondary"
                              className="text-[10px] font-medium py-0.5 px-2 rounded-md cursor-help"
                              data-testid={`badge-recommended-angle-${angle.id}`}
                            >
                              {angle.name}
                            </Badge>
                          );
                          if (!angle.description) {
                            return <span key={`${angle.id}-${i}`}>{badge}</span>;
                          }
                          return (
                            <Tooltip key={`${angle.id}-${i}`}>
                              <TooltipTrigger asChild>
                                <span onClick={(e) => e.stopPropagation()}>{badge}</span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs text-xs leading-relaxed"
                                data-testid={`tooltip-recommended-angle-${angle.id}`}
                              >
                                <p className="font-semibold mb-1">{angle.name}</p>
                                <p className="text-muted-foreground">{angle.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </TooltipProvider>
                  </div>
                )}

                {stage.transition_to_next_stage && (
                  <div className="flex items-start gap-2 rounded-lg bg-primary/[0.04] border border-primary/10 p-3">
                    <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <span className="text-[10px] font-semibold text-primary/70 uppercase tracking-wider">
                        Transition to Next Stage
                      </span>
                      <p className="text-xs text-foreground/75 mt-1 leading-relaxed">{stage.transition_to_next_stage}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function StageDetailField({ icon: Icon, label, value }: { icon: typeof Brain; label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/30 p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-[12px] text-foreground/80 leading-relaxed">{value}</p>
    </div>
  );
}

function getValidationErrors(
  product: Product | null,
  persona: Persona | null,
  brand: Brand | null,
  opp: NarrativeOpportunity | null,
  angles: NarrativeAngle[],
): string[] {
  const errors: string[] = [];
  if (!brand) errors.push("Brand data is loading...");
  if (!product?.name) errors.push("Select a product first");
  if (!persona?.name) errors.push("Select a persona first");
  if (!opp) errors.push("Select a narrative opportunity first");
  if (!angles || angles.length === 0) errors.push("Select at least one narrative angle");
  return errors;
}

function groupStagesByGroup(stages: FunnelStrategyData["stages"]): [string, FunnelStrategyData["stages"]][] {
  const groups = new Map<string, FunnelStrategyData["stages"]>();
  for (const stage of stages) {
    const group = stage.stage_group || "Other";
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(stage);
  }
  return Array.from(groups.entries());
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs < 60000) return "Generated just now";
    if (diffMs < 3600000) return `Generated ${Math.floor(diffMs / 60000)}m ago`;
    return `Generated ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  } catch {
    return "";
  }
}

const FUNNEL_PHASES = [
  { name: "initialization" as const, startTime: 0, endTime: 1250, label: "Initializing" },
  { name: "input-mapping" as const, startTime: 1250, endTime: 3250, label: "Mapping Inputs" },
  { name: "synthesis" as const, startTime: 3250, endTime: 5750, label: "Strategic Synthesis" },
  { name: "output-formation" as const, startTime: 5750, endTime: 8250, label: "Funnel Formation" },
  { name: "completion" as const, startTime: 8250, endTime: Infinity, label: "Complete" },
];

const FUNNEL_STATUS_MESSAGES = [
  "Ingesting campaign context",
  "Mapping persona and product fit",
  "Resolving narrative-to-stage alignment",
  "Structuring cognitive and affective progression",
  "Assigning proof and persuasion roles",
  "Building stage-by-stage strategy framework",
  "Preparing funnel strategy output",
];
